import { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, GoogleMapProps, OverlayView, useJsApiLoader } from "@react-google-maps/api";
import { MapMarker } from "../../MapView.types";
import { buildMarkerNode } from "../../utils/buildMarkerNode";
import { boundsSignature, computeBoundsPoints, computeFitZoom } from "../../utils/computeBounds";
import { MapAdapterProps } from "../MapProviderAdapter.types";
import { googleMapStyle } from "./googleMapStyle";
import styles from "./GoogleMapAdapter.module.css";

const FALLBACK_CENTER = { lat: 37.7749, lng: -122.4194 };
// Close-in zoom used when the camera focuses on a single Stop (see
// `focusMarkerId` below) — tight enough to read as "this one pin", unlike
// the route-wide fit-to-all-pins view.
const STOP_FOCUS_ZOOM = 16;
// Matches the padding `fitBounds` used to receive as its second argument —
// kept as one constant so the all-pins view and its derived zoom
// (`computeFitZoom`) agree on how much breathing room pins get.
const FIT_PADDING = 56;

export type GoogleMapAdapterProps = MapAdapterProps & { apiKey: string };

/**
 * Google Maps implementation of the map adapter contract. Requires a real
 * `apiKey` to load Google's script — `MapView` only mounts this adapter
 * once a key is present (see MapView.tsx), so the sandbox never fires an
 * external network request toward Google by default.
 */
export const GoogleMapAdapter = ({
  markers,
  cameraMarkers,
  focusMarkerId,
  etaOverlay,
  apiKey,
  className,
}: GoogleMapAdapterProps) => {
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey });
  const [map, setMap] = useState<google.maps.Map | null>(null);

  // `<GoogleMap>` (from `@react-google-maps/api`) treats `center` as a live,
  // reactive prop: on every update it does `nextCenter !== prevCenter` by
  // *reference* and calls `map.setCenter()` again if that fails — even when
  // the lat/lng is identical. `order` (hence every marker's `position`
  // object) gets a brand-new reference on every Redux-driven re-render —
  // including unrelated socket pushes (driver location, ETA, status) — via
  // `useOrderSummary`/`toOrderSummary`, which aren't memoized. Passing
  // `cameraMarkers[0]?.position` straight into `center` therefore snapped
  // the camera back to that one pin (Pickup, almost always) on every such
  // push, undoing `fitBounds` or the customer's own pan/zoom. Capturing it
  // once, lazily, here freezes the reference for this component's whole
  // lifetime — `center` becomes purely "initial center", matching how
  // react-leaflet's own `MapContainer` already only reads `center`/`zoom`
  // once at construction (see `OsmMapAdapter`). All positioning after mount
  // goes through the effects below instead, which call the map instance's
  // own methods directly and never touch this prop.
  const [initialCenter] = useState(() => cameraMarkers[0]?.position ?? FALLBACK_CENTER);

  const onLoad = useCallback((instance: google.maps.Map) => setMap(instance), []);
  const onUnmount = useCallback(() => setMap(null), []);

  // Camera management: a single effect that always knows exactly one camera
  // target — either the focused Stop, or the all-pins view — and applies it
  // exactly once per target change, never fighting itself:
  //  - No Stop focused: target is the all-pins view, i.e. what `fitBounds`
  //    would produce — computed via `computeFitZoom` rather than calling
  //    `fitBounds` itself so it can be *animated* to (see below), not jumped
  //    to. Only recomputed when *which* markers are on the map changes
  //    (load, driver appearing), never on a live position update — see
  //    `boundsSignature`.
  //  - A Stop is focused: target is that one marker, at `STOP_FOCUS_ZOOM`.
  // Either way, the very first time this runs for a mounted map, the target
  // is applied directly (`hasCenteredRef` still false — there's nothing to
  // animate from, it's establishing the initial view); every change after
  // that smoothly pans/zooms the existing map instance to the new target via
  // `panTo` — never another `fitBounds` call, so returning from a Stop
  // Details page smoothly zooms back out to the all-pins view instead of
  // staying stuck on the last-focused pin, and moving directly between Stops
  // smoothly transitions rather than jumping.
  const markersRef = useRef(markers);
  markersRef.current = markers;
  const cameraMarkersRef = useRef(cameraMarkers);
  cameraMarkersRef.current = cameraMarkers;
  const hasCenteredRef = useRef(false);
  const signature = boundsSignature(cameraMarkers);

  useEffect(() => {
    if (!map) return;

    const applyCamera = (center: google.maps.LatLngLiteral, zoom: number): void => {
      if (hasCenteredRef.current) {
        map.panTo(center);
        map.setZoom(zoom);
      } else {
        map.setCenter(center);
        map.setZoom(zoom);
      }
      hasCenteredRef.current = true;
    };

    if (focusMarkerId) {
      const focused = markersRef.current.find((marker) => marker.id === focusMarkerId);
      if (!focused) return;
      applyCamera(focused.position, STOP_FOCUS_ZOOM);
      return;
    }

    const points = computeBoundsPoints(cameraMarkersRef.current.map((marker) => marker.position));
    if (points.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    const container = map.getDiv();
    const zoom = computeFitZoom(points, { width: container.offsetWidth, height: container.offsetHeight }, FIT_PADDING);
    applyCamera(bounds.getCenter().toJSON(), zoom);
    // Deliberately keyed on `signature`, not `cameraMarkers`/`markers` — see the doc comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature, focusMarkerId]);

  if (loadError) {
    return <div className={className ?? styles.placeholder}>Google Maps couldn't load — check the API key.</div>;
  }

  if (!isLoaded) {
    return <div className={className ?? styles.placeholder}>Loading map…</div>;
  }

  const options: GoogleMapProps["options"] = {
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER },
    styles: googleMapStyle,
  };

  return (
    <GoogleMap
      mapContainerClassName={className ?? styles.container}
      center={initialCenter}
      zoom={13}
      options={options}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {markers.map((marker) => (
        <MarkerOverlay
          key={marker.id}
          marker={marker}
          etaLabel={etaOverlay && etaOverlay.markerId === marker.id ? etaOverlay.label : null}
        />
      ))}
    </GoogleMap>
  );
};

const MarkerOverlay = ({ marker, etaLabel }: { marker: MapMarker; etaLabel: string | null }) => {
  const { node, size, anchor } = buildMarkerNode(marker, etaLabel);

  return (
    <OverlayView
      position={marker.position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={() =>
        anchor === "center" ? { x: -size[0] / 2, y: -size[1] / 2 } : { x: -size[0] / 2, y: -size[1] }
      }
    >
      {node}
    </OverlayView>
  );
};
